import { useState, useCallback } from "react";
import { getAlternativeMeals } from "../lib/coach";
import { MACRO_GOALS } from "../data/meals";
import { todayKey } from "../utils/helpers";

const WATER_MEAL_ID = "__water__";

export default function TodayPlanSection({
  dailyPlan,
  todayMeals,
  allMeals,
  macroGoals,
  todayTotals,
  savedPlan,
  mealLog,
  onSavePlan,
  onResetPlan,
  onLogMeal,
  onRemove,
  onUpdateServings,
  onAddCustomMeal,
}) {
  const [swappingSlot, setSwappingSlot] = useState(null);
  const today = todayKey();

  // Get alternatives for the currently swapping slot
  const swapAlternatives = swappingSlot
    ? getAlternativeMeals({
        allMeals: allMeals || [],
        slot: swappingSlot,
        currentMealId: dailyPlan.mealPlan.find((mp) => mp.slot === swappingSlot)?.meal?.id,
        excludeIds: dailyPlan.mealPlan
          .filter((mp) => mp.slot !== swappingSlot)
          .map((mp) => mp.meal?.id)
          .filter(Boolean),
        proGap: Math.max(0, (macroGoals || MACRO_GOALS).protein - (todayTotals?.protein || 0)),
        calGap: Math.max(0, (macroGoals || MACRO_GOALS).calories - (todayTotals?.calories || 0)),
        count: 3,
        mealLog,
      })
    : [];

  const swapMeal = useCallback(
    (slot, newMeal) => {
      const updated = {
        date: today,
        meals: (
          savedPlan?.meals ||
          dailyPlan.mealPlan.map((mp) => ({
            slot: mp.slot,
            mealId: mp.meal.id,
            name: mp.meal.name,
            calories: mp.meal.calories,
            protein: mp.meal.protein,
          }))
        ).map((m) =>
          m.slot === slot
            ? { slot, mealId: newMeal.id, name: newMeal.name, calories: newMeal.calories, protein: newMeal.protein }
            : m
        ),
      };
      onSavePlan(updated);
      setSwappingSlot(null);
    },
    [savedPlan, dailyPlan.mealPlan, today, onSavePlan]
  );

  // Water tracking
  const waterEntry = todayMeals.find((e) => e.mealId === WATER_MEAL_ID);
  const waterGlasses = waterEntry ? waterEntry.servings : 0;
  const waterOz = waterGlasses * 8;
  const waterGoalOz = dailyPlan.hydrationOz || 0;
  const waterGoalGlasses = waterGoalOz ? Math.round(waterGoalOz / 8) : 0;
  const waterGoalHit = waterGoalOz > 0 && waterOz >= waterGoalOz;

  const handleAddWater = () => {
    if (waterEntry) {
      if (onUpdateServings) onUpdateServings(waterEntry.id, waterGlasses + 1);
    } else {
      if (onLogMeal)
        onLogMeal(
          { id: WATER_MEAL_ID, name: "Water", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 },
          1
        );
    }
  };

  const handleRemoveWater = () => {
    if (!waterEntry || waterGlasses <= 0) return;
    if (waterGlasses <= 1) {
      if (onRemove) onRemove(waterEntry.id);
    } else {
      if (onUpdateServings) onUpdateServings(waterEntry.id, waterGlasses - 1);
    }
  };

  if (dailyPlan.mealPlan.length === 0 && !dailyPlan.trainingFocus) return null;

  return (
    <section className="mb-44" aria-label="Today's meal plan">
      <div className="row-between mb-8">
        {savedPlan ? (
          <>
            <p className="ds-meta">Today&apos;s meals</p>
            <button className="btn-reset text-11-muted" onClick={onResetPlan}>
              Reset
            </button>
          </>
        ) : (
          dailyPlan.mealPlan.length > 0 && (
            <p className="ds-meta">Tap a meal for detail or to swap it</p>
          )
        )}
      </div>
      <div className="plan-card">
        {dailyPlan.mealPlan.map(({ slot, meal, logged }, i) => (
          <div key={`plan-${slot}`}>
            <div
              className={`plan-row${logged ? " plan-row--logged" : ""}`}
              style={{
                borderBottom:
                  i < dailyPlan.mealPlan.length - 1 ||
                  dailyPlan.hydrationOz ||
                  swappingSlot === slot
                    ? "1px solid var(--border)"
                    : "none",
              }}
              onClick={() => {
                if (logged) return;
                setSwappingSlot(swappingSlot === slot ? null : slot);
              }}
            >
              <button
                className={`check-circle${logged ? " check-circle--checked" : ""}`}
                aria-checked={logged}
                aria-label={logged ? `Remove ${meal.name} from log` : `Log ${meal.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (logged) {
                    const entry = todayMeals.find((te) => te.mealId === meal.id);
                    if (entry && onRemove) onRemove(entry.id);
                  } else {
                    if (onLogMeal) {
                      const slotHour = slot === "Breakfast" ? 8 : slot === "Lunch" ? 12 : 18;
                      const slotTs = new Date();
                      slotTs.setHours(slotHour, 0, 0, 0);
                      onLogMeal(meal, 1, slotTs.getTime());
                    }
                  }
                }}
              >
                {logged && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <div className="flex-1-min0">
                <div
                  className="label-xs mb-2"
                  style={{
                    letterSpacing: "1px",
                    color: logged ? "var(--success)" : "var(--text3)",
                  }}
                >
                  {slot}
                </div>
                <div
                  className="text-body text-truncate"
                  style={{ textDecoration: logged ? "line-through" : "none" }}
                >
                  {meal.name}
                </div>
                {/* The numbers are deliberately not on the resting row. A
                    calorie figure repeated three times down a wellness home
                    screen is the MyFitnessPal mechanic; here you have to ask
                    for it. It appears in the panel below on tap. */}
              </div>
              {!logged && (
                <svg
                  width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="var(--text3)" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, opacity: 0.5 }}
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17L17 7" />
                </svg>
              )}
            </div>

            {swappingSlot === slot && !logged && (
              <div
                className="swap-panel"
                style={{
                  borderBottom:
                    i < dailyPlan.mealPlan.length - 1 || dailyPlan.hydrationOz
                      ? "1px solid var(--border)"
                      : "none",
                }}
              >
                <p className="ds-meta" style={{ marginBottom: "var(--space-2)" }}>
                  {meal.name} — {meal.calories} cal &middot; {meal.protein}g protein.
                  Something else instead?
                </p>
                {swapAlternatives.map((alt) => (
                  <div
                    key={alt.id}
                    className="swap-option"
                    role="button"
                    tabIndex={0}
                    aria-label={`Swap to ${alt.name}`}
                    onClick={(e) => { e.stopPropagation(); swapMeal(slot, alt); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        swapMeal(slot, alt);
                      }
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="text-truncate" style={{ fontSize: 13 }}>{alt.name}</div>
                      <div className="mono text-11-muted">
                        {alt.calories} cal &middot; {alt.protein}g pro
                      </div>
                    </div>
                    <div className="swap-pick">Pick</div>
                  </div>
                ))}
                <div
                  className="swap-option swap-option--custom"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSwappingSlot(null);
                    if (onAddCustomMeal) onAddCustomMeal();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setSwappingSlot(null);
                      if (onAddCustomMeal) onAddCustomMeal();
                    }
                  }}
                >
                  <div className="text-sm" style={{ color: "var(--text2)" }}>
                    + Create custom meal
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {waterGoalOz > 0 && (
          <div className="flex-center" style={{ gap: 14, padding: "14px 16px" }} role="group" aria-label="Water tracker">
            <div className={`check-circle${waterGoalHit ? " check-circle--checked" : ""}`} aria-hidden="true">
              {waterGoalHit && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="flex-1-min0">
              <div
                className="label-xs mb-2"
                style={{
                  letterSpacing: "1px",
                  color: waterGoalHit ? "var(--success)" : "var(--text3)",
                  transition: "color 0.2s ease",
                }}
              >
                Water
              </div>
              <div className="text-body">
                {waterOz} oz &middot; {waterGlasses} {waterGlasses === 1 ? "glass" : "glasses"}
              </div>
              <div
                className="mono text-11-muted"
                style={{ color: waterGoalHit ? "var(--success)" : undefined }}
              >
                {waterGoalHit
                  ? `Goal reached \u00B7 ${waterGoalOz} oz`
                  : `${waterGoalOz - waterOz} oz left \u00B7 goal ${waterGoalGlasses} glasses`}
              </div>
            </div>
            <div className="flex-center-gap-6 flex-shrink-0">
              <button
                className="water-btn water-btn--minus"
                onClick={handleRemoveWater}
                disabled={waterGlasses <= 0}
                aria-label="Remove one glass of water"
              >
                −
              </button>
              <button
                className={`water-btn water-btn--plus${waterGoalHit ? " water-btn--plus-done" : ""}`}
                onClick={handleAddWater}
                aria-label="Add one glass of water"
              >
                +
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
